import React, { useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";

import PropTypes from "prop-types";

import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import {
    Button,
    FormControlLabel,
    Checkbox,
    TextareaAutosize,
    FormControl,
    FormLabel,
    RadioGroup,
    Radio,
} from "@mui/material";

import InputsFromFile from "../InputsFromFile/InputsFromFile";
import stylesSx from "./styles";
import { VISIBILITY } from "utils/constants";

const errorMap = {
    "file-invalid-type": "Formato de archivo no válidos, debe ser .pdf o .csv",
    "file-too-large": "El archivo es demasiado grande. El tamaño máximo permitido es 10 MB.",
};

const FileDropzone = ({
    file,
    inputsFromCSV = [],
    title = "Arrastrá o agregá tu archivo!",
    onDrop,
    open,
    setInputsFromUser,
    inputsFromUser,
    hasHeaderCallback,
    setVisibilityCallback,
    setDescripctionCallback,
}) => {
    const { baseStyle, focusedStyle, acceptStyle, rejectStyle, backDrop } = stylesSx;
    const [hasHeaders, setHasHeaders] = useState(false);
    const [visibility, setVisibility] = useState(VISIBILITY.public);
    const [description, setDescription] = useState("");
    const [errorMessages, setErrorMessages] = useState([]);

    const maxFileSize = 10 * 1024 * 1024; // 10MB (en bytes)

    const acceptedFiles = ["csv", "pdf"];

    const {
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject,
        acceptedFiles: droppedFiles,
        fileRejections,
    } = useDropzone({
        onDrop,
        accept: {
            "application/pdf": [".pdf"],
            "text/csv": [".csv"],
        },
        maxSize: maxFileSize,
    });

    const handleChangeVisibility = (event) => {
        setVisibility(event.target.value);
        setVisibilityCallback(event.target.value);
    };

    const style = useMemo(
        () => ({
            ...baseStyle,
            ...(isFocused ? focusedStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
        }),
        [isFocused, isDragAccept, isDragReject]
    );

    const handleCheckHasHeaders = (event) => {
        setHasHeaders(event.target.checked);
        hasHeaderCallback(event.target.checked);
    };

    useEffect(() => {
        if (fileRejections.length > 0) {
            const messages = fileRejections.flatMap(({ file, errors }) =>
                errors.map((e) => `${errorMap[e.code] || e.message}`)
            );
            setErrorMessages(messages);
        }
    }, [fileRejections]);

    useEffect(() => {
        if (file !== null) {
            setErrorMessages([]);
        }
    }, [file]);

    return (
        <div>
            <div {...getRootProps({ style })}>
                <input {...getInputProps()} />
                <p style={{ textAlign: "center" }}>
                    {title} <br />
                    <div style={{ fontSize: "12px" }}>
                        <i>
                            (Tipo de archivos permitidos:{" "}
                            {acceptedFiles.map((extension) => extension).join(" / ")} - Tamaño
                            máximo: {maxFileSize / 1024 / 1024} MB)
                        </i>
                    </div>
                </p>
                {errorMessages.length > 0 && (
                    <ul style={{ color: "red", fontSize: "12px", paddingLeft: "20px" }}>
                        {errorMessages.map((msg, index) => (
                            <li key={index}>{msg}</li>
                        ))}
                    </ul>
                )}
            </div>
            {file !== null ? (
                <div>
                    <ul>
                        <li>{file?.name}</li>
                    </ul>
                    {file?.type.includes("csv") && inputsFromCSV.length !== 0 && (
                        <div>
                            <FormControlLabel
                                label="La primera fila contiene los títulos?"
                                control={
                                    <Checkbox
                                        value=""
                                        checked={hasHeaders}
                                        onChange={handleCheckHasHeaders}
                                        color="primary"
                                    />
                                }
                            />
                            <h4>Elija el nombre de los campos de su csv</h4>
                            <InputsFromFile
                                hasHeaders={hasHeaders}
                                file={inputsFromCSV}
                                setInputsFromUser={setInputsFromUser}
                                inputsFromUser={inputsFromUser}
                            />
                        </div>
                    )}
                    <FormControl>
                        <FormLabel id="visilibty">Visibilidad</FormLabel>
                        <RadioGroup
                            row
                            required
                            aria-labelledby="demo-row-radio-buttons-group-label"
                            name="row-radio-buttons-group"
                            value={visibility}
                            onChange={handleChangeVisibility}
                        >
                            <FormControlLabel
                                value={VISIBILITY.public}
                                control={<Radio />}
                                label="Establecimiento"
                            />
                            <FormControlLabel
                                value={VISIBILITY.private}
                                control={<Radio />}
                                label="Privado"
                            />
                            <FormControlLabel
                                value={VISIBILITY.community}
                                control={<Radio />}
                                label="Comunidad"
                            />
                        </RadioGroup>
                    </FormControl>
                    <h4>Agregar descripción al dataset</h4>
                    <TextareaAutosize
                        style={{ width: "100%" }}
                        maxRows={6}
                        minRows={4}
                        value={description}
                        placeholder="Se recomienda agregar una descripción"
                        onChange={(evt) => {
                            setDescripctionCallback(evt.target.value);
                            setDescription(evt.target.value);
                        }}
                    />
                </div>
            ) : (
                <></>
            )}
            <Backdrop sx={backDrop} open={open}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </div>
    );
};

FileDropzone.propTypes = {
    inputsFromCSV: PropTypes.array.isRequired,
    title: PropTypes.string,
    onDrop: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    setInputsFromUser: PropTypes.func.isRequired,
    inputsFromUser: PropTypes.object.isRequired,
};

export default FileDropzone;
